import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/CloudDownload";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  HoldedContactPreview,
  HoldedContactSummary,
  useLazyGetHoldedContactPreviewQuery,
  useSearchHoldedContactsQuery,
} from "../../store/apis/companyApi";
import * as Sentry from "@sentry/react";

import OrbLoader from "../common/OrbLoader";
interface HoldedContactPickerProps {
  /** ID de contacto de Holded actual, si el billing ya está enlazado. */
  holdedContactId: string;
  /** Se llama al escribir o al elegir un contacto en el buscador. */
  onChangeContactId: (contactId: string) => void;
  /** Datos fiscales traídos de Holded, listos para volcar en el formulario. */
  onFill: (preview: HoldedContactPreview) => void;
  disabled?: boolean;
}

/**
 * Trae los datos fiscales de un contacto de Holded ANTES de guardar, para no teclearlos a mano y
 * que el sync posterior no pise nada. Dos caminos al mismo sitio: buscar el contacto por razón
 * social/NIF/email, o pegar su ID si ya se conoce.
 *
 * Solo lee: el volcado a los campos lo decide el formulario que lo usa, vía `onFill`.
 */
const HoldedContactPicker: React.FC<HoldedContactPickerProps> = ({
  holdedContactId,
  onChangeContactId,
  onFill,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // El buscador dispara una consulta por pulsación; sin este respiro cada letra
  // sería una llamada al backend (y a las tres cuentas de Holded detrás).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Solo se consulta con el desplegable abierto: si no, cada formulario de
  // billing en pantalla pediría el catálogo entero al montarse.
  const {
    data: contactsData,
    isFetching: isSearching,
    error: searchError,
  } = useSearchHoldedContactsQuery(
    { search: debouncedSearch, limit: 25 },
    { skip: !open }
  );

  // Un catálogo vacío y una búsqueda que ha fallado son cosas distintas, y con el
  // mismo "no se encontraron contactos" para ambas no hay forma de saber cuál es.
  const searchErrorMessage = searchError
    ? (searchError as { data?: { message?: string } })?.data?.message ||
      t("billing.holdedSearchError")
    : null;

  const [fetchPreview, { isFetching: isFetchingPreview }] =
    useLazyGetHoldedContactPreviewQuery();

  const options = useMemo<HoldedContactSummary[]>(
    () => contactsData?.data ?? [],
    [contactsData]
  );

  // El catálogo puede estar incompleto: una de las tres cuentas de Holded caída, o una
  // que devolvió una lista a medias. Sin decirlo, "no aparece" se lee como "no existe en
  // Holded" y manda a buscar el problema al sitio equivocado.
  const catalogueWarning = contactsData?.warning ?? null;

  const applyContact = async (contactId: string) => {
    const trimmedId = contactId.trim();
    if (!trimmedId) return;

    try {
      const response = await fetchPreview(trimmedId).unwrap();
      if (!response.data) {
        dispatch(
          setToastMessage({
            message: response.message || t("billing.holdedFetchError"),
            severity: "error",
          })
        );
        return;
      }

      onChangeContactId(trimmedId);
      onFill(response.data);
      dispatch(
        setToastMessage({
          message: t("billing.holdedFetchSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        t("billing.holdedFetchError");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  const roundedInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 50,
      fontFamily: "Nunito",
      fontSize: 16,
      "& fieldset": { borderColor: theme.palette.neutral[400] },
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        border: `1px dashed ${theme.palette.neutral[400]}`,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Nunito",
          fontWeight: 700,
          color: theme.palette.grey[900],
        }}
      >
        {t("billing.holdedPickerTitle")}
      </Typography>
      <Typography variant="body2" color={theme.palette.grey[700]}>
        {t("billing.holdedPickerHelp")}
      </Typography>

      <Autocomplete<HoldedContactSummary>
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        options={options}
        loading={isSearching}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.name || option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onInputChange={(_event, value) => setSearch(value)}
        onChange={(_event, value) => {
          if (value) void applyContact(value.id);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography sx={{ fontFamily: "Nunito", fontWeight: 700 }}>
                {option.name || option.id}
              </Typography>
              <Typography variant="body2" color={theme.palette.grey[700]}>
                {[option.nif, option.city, option.email]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </li>
        )}
        noOptionsText={searchErrorMessage ?? t("billing.holdedNoContacts")}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("billing.holdedSearchPlaceholder")}
            variant="outlined"
            sx={roundedInputSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching ? (
                    <OrbLoader size="inline" state="searching" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {searchErrorMessage && (
        <Typography variant="body2" color={theme.palette.error.main}>
          {searchErrorMessage}
        </Typography>
      )}

      {!searchErrorMessage && catalogueWarning && (
        <Typography variant="body2" color={theme.palette.warning.main}>
          {catalogueWarning}
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          value={holdedContactId}
          onChange={(event) => onChangeContactId(event.target.value)}
          disabled={disabled}
          fullWidth
          variant="outlined"
          placeholder={t("billing.holdedContactIdPlaceholder")}
          sx={roundedInputSx}
        />
        <Button
          onClick={() => void applyContact(holdedContactId)}
          disabled={disabled || !holdedContactId.trim() || isFetchingPreview}
          variant="outlined"
          startIcon={
            isFetchingPreview ? (
              <OrbLoader size="inline" state="connecting" />
            ) : (
              <DownloadIcon />
            )
          }
          sx={{
            borderRadius: 50,
            textTransform: "none",
            fontFamily: "Nunito",
            whiteSpace: "nowrap",
          }}
        >
          {t("billing.holdedFetchData")}
        </Button>
      </Box>
    </Box>
  );
};

export default HoldedContactPicker;
